/**
 * The office: who takes the calls, and who configures the company.
 *
 * On the fleet page rather than a section of its own, because what this page is actually
 * about is the people and cars a firm runs on — and splitting six dispatchers onto a page of
 * their own would be a sidebar entry somebody opens twice a year.
 *
 * Inviting is the point. Granting a role has always needed a user id, so adding a dispatcher
 * meant "tell them to register, wait, find their id, then grant" — with the finding done
 * against a database. An address is what an office actually has, and the person invited
 * chooses their own password from the link, exactly as the administrator did.
 *
 * It loads and saves on its own rather than through the page's refetch: the staff come from
 * the identity module by role, not from the fleet, and nothing the rest of the page does can
 * change them.
 */

import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { refusal } from '../errors';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  FieldErrors,
  Icon,
  Input,
  Section,
  Switch,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';
import { deskPhonesApi } from '../telephony/deskPhones';
import { DispatcherTelephoneDialog } from './DispatcherTelephoneDialog';
import { useFadingNote } from './fading';
import { staffApi, staffRoles, type StaffMember, type StaffRole } from './staff';

export function StaffPanel() {
  const { t } = useTranslation();
  const form = useId();

  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<StaffRole[]>(['Dispatcher']);
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState<StaffMember | null>(null);

  /** Whose telephone is open, and whether this firm has telephones at all. */
  const [telephoneOf, setTelephoneOf] = useState<StaffMember | null>(null);
  const [hasTelephony, setHasTelephony] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);
  const [note, setNote] = useFadingNote();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setStaff(await staffApi.list());
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  // Asked rather than assumed: a firm that did not buy telephony answers no, and the button
  // that would open an empty dialog is not drawn.
  useEffect(() => {
    void deskPhonesApi
      .handling()
      .then(() => setHasTelephony(true))
      .catch(() => setHasTelephony(false));
  }, []);

  async function invite(event: FormEvent) {
    event.preventDefault();

    setBusy(true);
    setError(null);
    setNote(null);

    try {
      const invited = await staffApi.invite({
        email: email.trim(),
        roles,
        displayName: displayName.trim() === '' ? null : displayName.trim(),
      });

      // Whether anything was sent depends on whether the address already had a password,
      // which the office cannot know. Saying so beats leaving somebody waiting for an
      // email that was never sent.
      setNote(
        invited.inviteExpiresAt === null
          ? t('fleet.staff.existingAccount', { email: invited.email })
          : t('fleet.staff.invited', { email: invited.email }),
      );

      // The dialog goes only once the invitation is away. What was typed survives a refusal
      // — a mistyped address is corrected rather than retyped.
      setEmail('');
      setDisplayName('');
      setInviting(false);
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Takes somebody's authenticator away so they enrol a new one.
   *
   * Confirmed first, and with their name in the sentence, because the mistake it prevents is
   * pressing it on the wrong row — and the consequence is somebody stranded outside the
   * console until they can find their phone and a quiet minute.
   */
  async function resetMfa(member: StaffMember) {
    const name = member.displayName ?? member.userId;

    setResetting(null);
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      await staffApi.resetMfa(member.userId);
      setNote(t('mfa.resetDone', { name }));
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  async function suspend(member: StaffMember) {
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      await staffApi.suspend(member.userId);
      setNote(t('fleet.staff.suspended', { name: member.displayName ?? member.userId }));
      await load();
    } catch (failure) {
      setError(refusal(failure, t, 'fleet.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <Section
        title={t('fleet.staff.title')}
        description={t('fleet.staff.help')}
        actions={
          <Button size="sm" disabled={busy} onClick={() => setInviting(true)}>
            <Icon name="plus" small />
            {t('fleet.staff.inviting')}
          </Button>
        }
      >
        {(error !== null || note !== null) && (
          <div className="flex flex-col gap-2 px-5 pt-3">
            {note !== null && <Banner tone="ok">{note}</Banner>}
            {error !== null && <Banner tone="danger">{error.message}</Banner>}
          </div>
        )}

        {staff === null ? (
          <EmptyState>{t('app.loading')}</EmptyState>
        ) : staff.length === 0 ? (
          <EmptyState>{t('fleet.staff.none')}</EmptyState>
        ) : (
          <Table>
            <thead>
              <Tr>
                <Th>{t('fleet.columns.name')}</Th>
                <Th>{t('fleet.columns.roles')}</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <Tr key={member.userId}>
                  <Td wrap>{member.displayName ?? member.userId}</Td>
                  <Td wrap>
                    {member.roles.map((role) => t(`fleet.staff.role.${role}`)).join(' · ')}
                  </Td>
                  <Td>
                    <span className="flex justify-end gap-1.5">
                      {hasTelephony && (
                        <Button size="sm" disabled={busy} onClick={() => setTelephoneOf(member)}>
                          {t('fleet.telephone.open')}
                        </Button>
                      )}

                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => setResetting(member)}
                      >
                        {t('mfa.reset')}
                      </Button>

                      <Button
                        variant="danger"
                        size="sm"
                        disabled={busy}
                        onClick={() => void suspend(member)}
                      >
                        {t('fleet.staff.suspend')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}

        <Dialog
          open={inviting}
          title={t('fleet.staff.inviting')}
          onClose={() => setInviting(false)}
          actions={
            <>
              <Button variant="quiet" onClick={() => setInviting(false)}>
                {t('fleet.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                form={form}
                disabled={busy || roles.length === 0}
              >
                {t('fleet.staff.invite')}
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            {error !== null && <Banner tone="danger">{error.message}</Banner>}

            <form id={form} onSubmit={(event) => void invite(event)} className="flex flex-col gap-3">
              <Field id="email" label={t('fleet.staff.email')} help={t('fleet.staff.emailHelp')}>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </Field>

              <Field id="displayName" label={t('fleet.staff.name')}>
                <Input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </Field>

              <Field id="roles" label={t('fleet.staff.roles')}>
                <div className="flex flex-col gap-2">
                  {staffRoles.map((role) => (
                    <div key={role} className="flex items-center justify-between gap-3">
                      <span>{t(`fleet.staff.role.${role}`)}</span>
                      <Switch
                        checked={roles.includes(role)}
                        label={t(`fleet.staff.role.${role}`)}
                        onChange={(next) =>
                          setRoles(
                            next ? [...roles, role] : roles.filter((held) => held !== role),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </Field>
            </form>
          </div>
        </Dialog>

        <DispatcherTelephoneDialog member={telephoneOf} onClose={() => setTelephoneOf(null)} />

        <Dialog
          open={resetting !== null}
          title={t('mfa.reset')}
          onClose={() => setResetting(null)}
          actions={
            <>
              <Button variant="quiet" onClick={() => setResetting(null)}>
                {t('fleet.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={busy || resetting === null}
                onClick={() => {
                  if (resetting !== null) void resetMfa(resetting);
                }}
              >
                {t('mfa.reset')}
              </Button>
            </>
          }
        >
          <p className="text-body m-0">
            {t('mfa.resetConfirm', {
              name: resetting?.displayName ?? resetting?.userId ?? '',
            })}
          </p>
        </Dialog>
      </Section>
    </FieldErrors>
  );
}
